/**
 * Created by kmiltiadous on 20/07/2017.
 */
import {Inject, Injectable, InjectionToken} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {NotificationsService} from '@westjetairlines/wjesd-component-library';
import {isNullOrUndefined} from 'util';
import {Observable} from 'rxjs/Observable';
import {NavigationService} from 'app/layout/index';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {TRAVEL_CREDIT_SERVICE_TOKEN} from './travel-credit.service';
import {GuestCredit} from '../models/guest-credit';
import {Reservation} from '../models/reservation';
import {Iata} from '../models/iata';
import {Credit} from '../models/credit';
import {Agency} from '../models/agency';

export interface IWorkflowDataService {
  getReservation: BehaviorSubject<Reservation>;
  getTicketingIata: BehaviorSubject<Iata>;
  getAgency: BehaviorSubject<Agency>;
  getCredit: BehaviorSubject<Credit>;
  getGuestCredits: BehaviorSubject<Array<GuestCredit>>;

  setReservation(data: Reservation);
  setCredit(data: Credit);
  setGuestCredits(data: Array<GuestCredit>);
  setAgency(data: Agency);
  save(): Observable<any>;
  initializeTravelCredit(id: number): void;
  setTicketingIata(data: Iata);
}

export const WORKFLOW_DATA_SERVICE_TOKEN = new InjectionToken<IWorkflowDataService>('workflow-data.service');

@Injectable()
export class WorkflowDataService implements IWorkflowDataService {
  private id: number = null;
  private reservation: Reservation = null;
  private credit: Credit = null;
  private guestCredits: Array<GuestCredit> = null;
  private agency: Agency = null;
  private iata: Iata = null;

  public getReservation: BehaviorSubject<Reservation> = new BehaviorSubject<Reservation>(this.reservation);
  public getCredit: BehaviorSubject<Credit> = new BehaviorSubject<Credit>(this.credit);
  public getTicketingIata: BehaviorSubject<Iata> = new BehaviorSubject<Iata>(this.iata);
  public getAgency: BehaviorSubject<Agency> = new BehaviorSubject<Agency>(this.agency);
  public getGuestCredits: BehaviorSubject<Array<GuestCredit>> = new BehaviorSubject<Array<GuestCredit>>(this.guestCredits);

  constructor(@Inject(TRAVEL_CREDIT_SERVICE_TOKEN) private travelCreditService, private navigationService: NavigationService,
              private notificationService: NotificationsService) {
    this.navigationService.createAnnounced.subscribe(() => {
      this.notificationService.clear();
      this.reservation = null;
      this.credit = null;
      this.guestCredits = null;
      this.agency = null;
      this.iata = null;
      this.id = null;

      this.getReservation.next(this.reservation);
      this.getTicketingIata.next(this.iata);
      this.getAgency.next(this.agency);
      this.getCredit.next(this.credit);
      this.getGuestCredits.next(this.guestCredits);
    });
  }

  setReservation(data: Reservation) {
    this.reservation = data;
    this.guestCredits = data.guestCredits;

    this.getReservation.next(this.reservation);
    this.getGuestCredits.next(this.guestCredits);
  }

  setCredit(data: Credit) {
    this.credit = data;
    this.getCredit.next(this.credit);

    if (isNullOrUndefined(this.guestCredits)) {
      return;
    }

    for (const guestCredit of this.guestCredits) {
      if (!isNullOrUndefined(data.feeAmountWaived)) {
        guestCredit.feeAmountWaived = data.feeAmountWaived;
      }
      if (!isNullOrUndefined(data.valueWaived)) {
        guestCredit.valueWaived = data.valueWaived;
      }
    }

    this.getGuestCredits.next(this.guestCredits);
  }

  setGuestCredits(data: Array<GuestCredit>) {
    if (this.reservation != null) {
      this.reservation.guestCredits = data;
      this.getReservation.next(this.reservation);
    }

    this.guestCredits = data;
    this.getGuestCredits.next(this.guestCredits);
  }

  setAgency(data: Agency) {
    this.agency = data;
    this.getAgency.next(this.agency);
  }

  setTicketingIata(data: Iata) {
    this.iata = data;
    this.getTicketingIata.next(this.iata);
  }

  save(): Observable<any> {
    if (this.id) {
      return this.travelCreditService.updateTravelCredit(this.id, this.reservation, this.credit, this.guestCredits[0], this.agency, this.iata);
    } else {
      return this.travelCreditService.createTravelCredits(this.reservation, this.credit, this.guestCredits, this.agency, this.iata);
    }
  }

  initializeTravelCredit(id: number): void {
    this.travelCreditService.getTravelCredit(id).subscribe(data => {
      if (data.wasSuccessful) {
        let travelCredit = data.value;
        let decimalPipe = new DecimalPipe('en-US');
        let feeAmountWaived = decimalPipe.transform(travelCredit.feeAmountWaived, '1.2-2').replace(/,/g , "");
        let valueWaived = decimalPipe.transform(travelCredit.valueWaived, '1.2-2').replace(/,/g , "");
        let guestCredit: GuestCredit = new GuestCredit(travelCredit.guestFirstName, travelCredit.guestLastName,
          travelCredit.ticketNumber, feeAmountWaived, valueWaived)
        let guestCredits: Array<GuestCredit> = new Array<GuestCredit>();
        guestCredits.push(guestCredit);

        this.id = id;
        this.guestCredits = guestCredits;
        this.getGuestCredits.next(this.guestCredits);
        this.reservation = new Reservation(travelCredit.pnr, travelCredit.wviBookingId, guestCredits);
        this.getReservation.next(this.reservation);
        this.credit = new Credit(travelCredit.reasonCode, travelCredit.refundReason, travelCredit.currency,
          travelCredit.fareMasked, feeAmountWaived, valueWaived, travelCredit.travelCreditType);
        this.getCredit.next(this.credit);
        this.agency = new Agency(travelCredit.travelAgencyName, travelCredit.travelAgentName,
          travelCredit.comments, travelCredit.salesRep, travelCredit.team);
        this.getAgency.next(this.agency);
        this.iata = travelCredit.iata;
        this.getTicketingIata.next(this.iata);
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/workflow-data.service.ts